import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Range, PowersService, InverterV2 } from 'ngx-core-services';
import { map, catchError, withLatestFrom, mergeMap } from 'rxjs/operators';
import { of, from } from 'rxjs';

import {
  AppState,
  SystemsState,
  getSystemDefinitionConfiguration,
  requestSystemDeviceDailyPowers,
  setSystemDeviceDailyPowersError,
  updateSystemDeviceDailyPowers,
} from '..';
import { getPowerTelemetry } from './effects.utils';

@Injectable()
export class DeviceDailyPowersEffects {

  requestDeviceDailyPowersState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestSystemDeviceDailyPowers),
      withLatestFrom(this.store),
      mergeMap(([action, { systemState }]) =>
        from(this.getDeviceDailyPowers(
            action.systemId,
            action.deviceId,
            action.range,
            action.forceRefresh,
            systemState,
          )
        ).pipe(
          map((deviceDailyPowers) => updateSystemDeviceDailyPowers({
            systemId: action.systemId,
            instanceId: action.instanceId,
            range: action.range,
            deviceId: action.deviceId,
            systemPowerState: deviceDailyPowers
          })),
          catchError(_ => of(setSystemDeviceDailyPowersError({ systemId: action.systemId, instanceId: action.instanceId, deviceId: action.deviceId, error: 'Failed to fetch system energy state' })))
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<AppState>,
    private readonly powersService: PowersService,
  ) {
    this.getDeviceDailyPowers = this.getDeviceDailyPowers.bind(this);
  }

  async getDeviceDailyPowers(systemId: string, deviceId: string, range: Range, forceRefresh: boolean, state: SystemsState): Promise<Partial<InverterV2>[]>  {
    const system = state.systemList[systemId];

    if (!system) {
      throw new Error(`System with id ${systemId} not found`);
    }

    const systemConfiguration = getSystemDefinitionConfiguration(system);
    const rawPowers = await this.powersService.fetchDeviceAllPowers(deviceId, systemConfiguration.monitoringType, range.from, forceRefresh, `${systemConfiguration.tz}`);
    return getPowerTelemetry(rawPowers, !!systemConfiguration.inverterMeterSensor, deviceId);
  }
}
